import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useState } from "react";
import {
  formatShortTimestamp as formatShortTimestampIn,
  type TimezonePreference,
} from "@/lib/format";

const STORAGE_KEY = "fleet-timezone";

interface TimezoneContextType {
  timezone: TimezonePreference;
  setTimezone: (timezone: TimezonePreference) => void;
  formatTimestamp: (isoString: string) => string;
  /** "16:30" today, "Sep 4, 09:00" otherwise — for near-future instants like a takeover window. */
  formatShortTimestamp: (isoString: string) => string;
}

const TimezoneContext = createContext<TimezoneContextType | null>(null);

function readStoredTimezone(): TimezonePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "utc" || stored === "local") return stored;
  } catch {
    // localStorage unavailable
  }
  return "local";
}

export function TimezoneProvider({ children }: { children: ReactNode }) {
  const [timezone, setTimezoneState] = useState<TimezonePreference>(readStoredTimezone);

  const setTimezone = useCallback((next: TimezonePreference) => {
    setTimezoneState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // localStorage unavailable
    }
  }, []);

  const formatTimestamp = useCallback(
    (isoString: string): string => {
      const date = new Date(isoString);
      const options: Intl.DateTimeFormatOptions = {
        ...(timezone === "utc" ? { timeZone: "UTC" } : {}),
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      };
      const formatted = date.toLocaleString("en-US", options);
      return timezone === "utc" ? `${formatted} UTC` : formatted;
    },
    [timezone],
  );

  const formatShortTimestamp = useCallback(
    (isoString: string): string => formatShortTimestampIn(isoString, timezone),
    [timezone],
  );

  return (
    <TimezoneContext.Provider
      value={{ timezone, setTimezone, formatTimestamp, formatShortTimestamp }}
    >
      {children}
    </TimezoneContext.Provider>
  );
}

export function useTimezone() {
  const ctx = useContext(TimezoneContext);
  if (!ctx) throw new Error("useTimezone must be used within TimezoneProvider");
  return ctx;
}
